import {PolymerElement, html} from '@polymer/polymer';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';
import {ScopeBinding} from '../behaviors/keli-binding-behavior.js';
import {HotkeyBehavior} from '../behaviors/hotkey-behavior.js';
import {TooltipBehavior} from '../behaviors/tooltip-behavior';
import {ResizeObserverBehavior} from '../behaviors/resize-observer-behavior.js';

class KeliGrid extends ScopeBinding(ResizeObserverBehavior(TooltipBehavior(HotkeyBehavior(PolymerElement)))) {
  static get template() {
    return html`
      <style>
        :host {
          --keli-input-field-height: auto;
          --keli-input-field-width: 100%;
          --keli-input-field-min-height: none;
          --keli-input-field-text-align: inherit;
          --keli-input-field-font-size: .9em;
          --keli-input-field-padding: 0;
          --keli-input-field-inner-padding: 2px;
          --keli-input-field-inner-height: auto;
          --keli-input-field-border: none;
          --keli-input-invalid-background-color: var(--keli-invalid-background);
          --keli-input-invalid-outline: 1px solid var(--keli-invalid-border);
          --keli-field-background: none;
          --keli-value-background-color: var(--keli-subtle-background);
          --keli-value-font-size: .93em;
          --keli-value-min-height: none;
          --keli-value-padding: 0;
          --keli-value-text-margin: 1px 0 0 0;
          --keli-value-width: 100%;
          outline: none;
          display: block;
        }

        sl-icon {
          font-size: 16px;
        }

        #header, #body {
          flex-direction: column;
          display: flex;
        }

        #header {
          font-size: .9em;
          font-weight: 700;
        }

        #header .cell {
          background-color: var(--keli-table-header-color);
          padding-top: 2px;
          text-indent: 2px;
          overflow: hidden;
          cursor: auto;
        }

        .row {
          display: flex;
        }

        .cell {
          flex: 1;
          display: flex;
          border: 1px solid var(--keli-table-border-color);
          margin-left: -1px;
          margin-bottom: -1px;
        }

        .cell.focused {
          border-color: var(--keli-field-border-color-active);
          background-color: var(--keli-subtle-background);
          --keli-input-field-background-color: 'transparent';
        }

        .cell.icon {
          width: 16px;
          padding: 2px 2px 0 2px;
          flex: 0 0 16px;
          color: var(--keli-field-icon-color);
          cursor: pointer;
        }

        .cell.icon:hover {
          color: var(--keli-field-icon-hover-color);
        }

        .insert .cell {
          border-top-width: 3px;
        }

        .insert .cell.icon {
          border-top-width: 3px;
          color: var(--keli-field-icon-hover-color);
        }
      </style>

      <keli-grid-controller id="controller" keliname="_t"></keli-grid-controller>

      <div id="header"></div>
      <div id="body"></div>`;
  }

  static get is() {
    return 'keli-grid';
  }

  static get properties() {
    return {
      AdjacentElement: {type: Object},
      focusInsert: {type: Boolean, scopevar: true},
      focused: {type: Boolean, observer: 'focusedChanged'},
      focusedCell: {type: Object, observer: 'focusedCellChanged'},
      iconDelete: {type: String, value: 'delete'},
      iconEdit: {type: String, value: 'open-in-app'},
      iconInsert: {type: String, value: 'plus-box'},
      insertable: {type: Boolean, scopevar: true, observer: 'insertableChanged'},
      insertRowHasChanges: {type: Boolean},
      isBlockPanelElement: {type: Boolean, value: true},
      keliControlTypes: {type: Array, value: ['grid']},
      lastFocusedInfo: {type: Object},
      needsDataLoaded: {type: Boolean, value: true},
      tooltipText: {type: String},
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._ensureAttribute('tabindex', '0');
    this.classList.add('focusable-control');

    this.addEventListener('focus', this.handleFocus);
    this.addEventListener('blur', this.handleBlur);
    this.addEventListener('dataUpdated', this.handleDataUpdated);
    this.addEventListener('focusElement', this.handleFocusElement);
    this.addEventListener('keliFocus', this.handleKeliFocus);
    this.addEventListener('keliAccepted', this.handleKeliAccepted);
    this.addEventListener('keliUserChange', this.handleKeliUserChange);
    this.addEventListener('activateRowAction', this.handleActivateRowAction);
    this.addEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.addEventListener('panelElementAttributeChanged', this.handlePanelElementAttributeChanged);

    let skipFieldStartedElement;
    this.AdjacentElement = new AdjacentElement({
      'this': this.shadowRoot,
      'selector': '.input-field',
      'properties': {disabled: false},
      'defaultCallback': (newElement, numericDirection) => {
        if (newElement && newElement.skip === true && numericDirection === 1 && skipFieldStartedElement !== newElement) {
          skipFieldStartedElement = skipFieldStartedElement || newElement;
          return this.AdjacentElement.next(newElement);
        }
        skipFieldStartedElement = undefined;

        if (!newElement || !newElement.dispatchEvent) {
          return;
        }

        // If we are "moving" to the same field, fire the save methods.
        if (numericDirection && newElement._keliAccept && newElement.focused) {
          newElement._keliAccept();
        }

        newElement.dispatchEvent(new CustomEvent('focusElement', {
          bubbles: true, composed: true,
          detail: {
            element: newElement,
            last: (numericDirection === -1)
          }
        }));
      }
    });

    this.registerHotkey(KeliInterface.Hotkeys.grid.next, {
      down: event => {
        // Allow tab to be used with any combo, except `shift`, since that's used for previous field.
        if (event.shiftKey) {
          return true;
        }
        if (this.AdjacentElement.currentElement?.fieldValueHasChanged && this._insert()?.contains(this.AdjacentElement.currentElement)) {
          this.insertRowHasChanges = true;
        }
        if (this.AdjacentElement.isLast && this._insert()?.contains(this.AdjacentElement.currentElement)) {
          if (!this.insertRowHasChanges) {
            return true;
          }
          return this.saveInsertRow(this.getFocusedRow()).then(() => this.focusInsertRow());
        }
        this.AdjacentElement?.next();
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.grid.previous, {
      down: () => {
        if (this.AdjacentElement.isFirst) {
          return true;
        }
        this.AdjacentElement?.previous();
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.grid.up, {
      down: async () => {
        await this.moveFocusedRow(true);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.grid.down, {
      down: async () => {
        await this.moveFocusedRow(false, true);
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.grid.insert, {
      down: async () => {
        const currentRow = this.getFocusedRow();
        if (currentRow && this._insert() === currentRow) {
          const toKeliName = this.AdjacentElement.currentElement.keliname;
          await this.saveInsertRow(currentRow);
          this.focusInsertRow(toKeliName);
        } else {
          this.focusInsertRow();
        }
      }
    });
    this.registerHotkey(KeliInterface.Hotkeys.grid.edit, {
      down: () => {
        const currentRow = this.getFocusedRow();
        if (currentRow && !this._insert() === currentRow) {
          this.$.controller.dispatchEvent(new CustomEvent('keliAccepted', {
            detail: {id: currentRow.getAttribute('row-id')}
          }));
        }
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('focus', this.handleFocus);
    this.removeEventListener('blur', this.handleBlur);
    this.removeEventListener('dataUpdated', this.handleDataUpdated);
    this.removeEventListener('focusElement', this.handleFocusElement);
    this.removeEventListener('keliFocus', this.handleKeliFocus);
    this.removeEventListener('keliAccepted', this.handleKeliAccepted);
    this.removeEventListener('keliUserChange', this.handleKeliUserChange);
    this.removeEventListener('activateRowAction', this.handleActivateRowAction);
    this.removeEventListener('setPanelActiveElement', this.handleSetPanelActiveElement);
    this.removeEventListener('panelElementAttributeChanged', this.handlePanelElementAttributeChanged);

    if (this.AdjacentElement) {
      this.AdjacentElement.destroy();
      this.AdjacentElement = null;
    }

    this.focusedCell = null;
  }

  async keliReady(scope) {
    super.keliReady && await super.keliReady(scope);
    await this.$.controller.elementReady(this);
  }

  _insert() {
    const insertRows = this.$.body.querySelectorAll('.row.insert');
    if (insertRows.length) {
      return insertRows[insertRows.length - 1];
    }
  }

  async focusedChanged(newValue) {
    this.classList.toggle('focused', newValue);
    if (newValue) {
      this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
        bubbles: true,
        composed: true,
        detail: {element: this}
      }));
    }
    this.resetHotkeyState();
  }

  focusedCellChanged(newValue, oldValue) {
    if (oldValue) {
      oldValue.classList.remove('focused');
    }
    if (newValue) {
      newValue.classList.add('focused');
    }
  }

  async insertableChanged(newValue) {
    if (newValue === false) {
      this._insert()?.remove();
    } else if (this.$.controller?.getKeliScope) { // Make sure the controller is set up.
      await this.$.controller.createInsertRow();
    }
  }

  handleFocus() {
    this.focused = true;
  }

  handleBlur() {
    this.focused = false;
    this.focusedCell = null;
  }

  handleElementResized() {
    const headerCells = this.$.header.querySelectorAll('.cell');
    for (const cell of headerCells) {
      const tooltip = this.getTooltipInstance(cell);
      if (tooltip) {
        if (cell.scrollWidth > cell.clientWidth) {
          tooltip.enable();
        } else {
          tooltip.disable();
        }
      }
    }
  }

  handleDataUpdated(event) {
    // Handle initial focus & initial data load.
    if (this.focused && event.detail.initial && !this.AdjacentElement.currentElement) {
      this.handleFocusElement(event);
      return;
    }
    if ((this.focused || event.detail.focus) && (event.detail.rowToSelect || this.lastFocusedInfo)) {
      this.focusOn(event.detail.rowToSelect || this.lastFocusedInfo.rowId, this.lastFocusedInfo.cellName);
    } else if (event.detail.rowToSelect) {
      const cellElement = this.focusOn(event.detail.rowToSelect, this.lastFocusedInfo?.cellName, true);
      if (cellElement) {
        this.handleKeliFocus({detail: {element: cellElement, updateOnly: true}});
      }
    }
  }

  handleFocusElement(event) {
    this.focus();
    if (event?.detail?.first) {
      if (this.insertable !== false && this.focusInsert) {
        this.focusInsertRow();
      } else {
        this.AdjacentElement.first();
      }
    } else if (event?.detail?.last) {
      this.AdjacentElement.last();
    } else {
      if (this.insertable !== false && this.focusInsert && !this.AdjacentElement.currentElement) {
        this.focusInsertRow();
      } else {
        this.AdjacentElement.current();
      }
    }
    this.resetHotkeyState();
  }

  handleKeliFocus(event) {
    this.lastFocusedInfo = {
      rowId: event.detail.element.boundKeliRowId,
      cellName: event.detail.element.keliname,
    };

    if (event.detail.updateOnly) {
      this.AdjacentElement.element = event.detail.element;
    } else {
      this.AdjacentElement.current(event.detail.element);
    }
    if (event.detail.element.boundKeliRowId) {
      this.$.controller.updateLastSelectedRow(event.detail.element.boundKeliRowId);
    }
    if (this.focused) {
      this.focusedCell = this.getFocusedCell();
    }
  }

  handleKeliAccepted(event) {
    if (this._insert()?.contains(event.detail.element)) {
      this.insertRowHasChanges = true;
    }
  }

  async handleActivateRowAction(event) {
    const isInsertRow = (this._insert()?.getAttribute('row-id') === event.detail.rowId);
    if (isInsertRow) {
      await this.saveInsertRow(this._insert());
    } else {
      this.$.controller.dispatchEvent(new CustomEvent('keliAccepted', {
        detail: {id: event.detail.rowId}
      }));
    }
  }

  handleSetPanelActiveElement(event) {
    if (event.detail.element !== this) {
      event.stopPropagation();
      this.handleFocus();
    }
  }

  handlePanelElementAttributeChanged(event) {
    if (!event?.detail) {
      return;
    }

    const focusedElement = this.AdjacentElement.currentElement;
    switch (event.detail.attribute) {
      case 'readOnly':
        if (event.detail.element === focusedElement && event.detail.element.readOnly === true) {
          this.AdjacentElement.next(focusedElement);
        }
        break;
      case 'skip':
        if (event.detail.element === focusedElement && event.detail.element.skip === true) {
          this.AdjacentElement.next(focusedElement);
        }
        break;
    }
  }

  async saveInsertRow(insertRow, createNewRow = true) {
    const focusedElement = this.AdjacentElement.currentElement;
    if (focusedElement) {
      await focusedElement._keliAccept();
    }

    if (!insertRow || !this.insertRowHasChanges || this._insert() !== insertRow) {
      return;
    }

    if (createNewRow) {
      if (this.focused) {
        this.focus();
      }

      this.insertRowHasChanges = false;
      const iconCell = insertRow.querySelector('.cell.icon');
      if (iconCell) {
        const iconElement = iconCell.querySelector('sl-icon');
        if (iconElement) {
          iconElement.name = this.iconEdit;
        }
      }
    }

    await this.$.controller.saveInsert();
    if (createNewRow) {
      this._insert()?.removeClass('insert');
      await this.$.controller.createInsertRow();
    }
  }

  focusInsertRow(focusOnKeliName = undefined, force = false) {
    if (!this.focused && !force) {
      return;
    }
    for (const element of this.AdjacentElement.nodeList) {
      if (this._insert()?.contains(element)) {
        if (!focusOnKeliName) {
          this.AdjacentElement.current(element);
          break;
        } else if (element.keliname === focusOnKeliName) {
          this.AdjacentElement.current(element);
          break;
        }
      }
    }
  }

  focusOn(rowId, fieldName = undefined, updateOnly = false) {
    let foundCell = false;
    let focusRow;
    if (rowId) {
      focusRow = this.shadowRoot.querySelector(`.row[row-id="${rowId}"]`);
    } else {
      focusRow = this.getFocusedRow() || this.$.body.querySelector('.row');
    }
    if (focusRow) {
      const focusFieldName = fieldName || this.AdjacentElement.currentElement?.keliname || this.AdjacentElement.firstElement?.keliname;
      const allFields = focusRow.querySelectorAll('.input-field');
      for (const field of allFields) {
        if (field.keliname === focusFieldName) {
          if (updateOnly) {
            this.AdjacentElement.element = field;
          } else {
            this.AdjacentElement.current(field);
          }
          foundCell = true;
          break;
        }
      }
    }
    if (!foundCell && !updateOnly) {
      this.dispatchEvent(new CustomEvent('focusElement'));
    }
    return this.AdjacentElement.currentElement;
  }

  async moveFocusedRow(up = false, down = false) {
    const currentId = this.AdjacentElement.currentElement?.boundKeliRowId;
    if (!currentId) {
      return;
    }

    const allRows = this.shadowRoot.querySelectorAll(`.row`);
    const focusedRowIndex = Array.from(allRows).findIndex(element => element.getAttribute('row-id') === currentId);

    if (up && focusedRowIndex === 1) { // Look for 1, because the header row is a ".row".
      this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
        bubbles: true,
        composed: true,
        detail: {element: this, previous: true}
      }));
      return;
    }
    if (down && focusedRowIndex === allRows.length - 1) {
      if (this.insertRowHasChanges) {
        const toKeliName = this.AdjacentElement.currentElement.keliname;
        await this.saveInsertRow(this.getFocusedRow());
        this.focusInsertRow(toKeliName);
      } else {
        this.dispatchEvent(new CustomEvent('setPanelActiveElement', {
          bubbles: true,
          composed: true,
          detail: {element: this, next: true}
        }));
      }
      return;
    }

    const newIndex = focusedRowIndex + (up ? -1 : 1);
    const newRow = allRows[newIndex];
    if (!newRow) {
      return;
    }
    const allFields = newRow.querySelectorAll('.input-field');
    for (const field of allFields) {
      if (field.keliname === this.AdjacentElement.currentElement.keliname) {
        this.AdjacentElement.current(field);
        break;
      }
    }
  }

  getFocusedRow() {
    const currentId = this.AdjacentElement?.currentElement?.boundKeliRowId;
    if (currentId) {
      return this.shadowRoot.querySelector(`.row[row-id="${currentId}"]`);
    }
  }

  getFocusedCell() {
    const field = this.AdjacentElement?.currentElement;
    if (field) {
      return field.closest('.cell');
    }
  }

  async _keliAccept(event, isClosingPanel) {
    if (this.AdjacentElement.currentElement?.fieldValueHasChanged && this._insert()?.contains(this.AdjacentElement.currentElement)) {
      this.insertRowHasChanges = true;
    }
    await this.AdjacentElement.currentElement?._keliAccept(event, isClosingPanel);
    await this.saveInsertRow(this.getFocusedRow(), false);
  }
}

customElements.define(KeliGrid.is, KeliGrid);
export {KeliGrid};